/**
 * IdentityShell — domain frame for Identity & Access.
 *
 * Renders the two sub-nav tabs (Users / Roles) and delegates to <Outlet />.
 * Header carries two action buttons:
 *   - [Sync]     opens a unified modal that reconciles Entra (field deltas +
 *                role-source-group membership) and Core Director (officer
 *                code suggestions, stale codes, name mismatches), shows the
 *                results as a single per-user finding list, and applies
 *                everything via one button.
 *   - [Ignored (N)]  opens the ignore/override modal with three sub-tabs
 *                that hide when their list is empty. Restore action removes
 *                a row from the ignore list.
 *
 * v0.8.6 (Bill 2026-05-15) — replaces the prior 3-tab IA (Users/Roles/Sync).
 */

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { DomainShell } from '@/components/DomainShell';
import { Button } from '@/components/primitives';
import { SyncModal } from './SyncModal';
import { IgnoredModal } from './IgnoredModal';

const SUBNAV = [
  {
    slug: 'users',
    label: 'Users',
    requiredRoles: ['SuperAdmin', 'Admin'] as const,
  },
  {
    slug: 'roles',
    label: 'Roles',
    requiredRoles: ['SuperAdmin', 'Admin'] as const,
  },
];

export function IdentityShell(): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const [syncOpen, setSyncOpen] = useState(false);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [ignoreCount, setIgnoreCount] = useState<number | null>(null);

  const refreshIgnoreCount = useCallback(async () => {
    try {
      const [f, g, s] = await Promise.all([
        client.retail.admin.listFieldSyncIgnores(),
        client.retail.admin.listGroupSyncIgnores(),
        client.retail.admin.listSubordinateOverrides(),
      ]);
      const total =
        (f.success ? f.data.length : 0) +
        (g.success ? g.data.length : 0) +
        (s.success ? s.data.length : 0);
      setIgnoreCount(total);
    } catch {
      setIgnoreCount(null);
    }
  }, [client]);

  useEffect(() => { void refreshIgnoreCount(); }, [refreshIgnoreCount]);

  // Refresh count whenever either modal closes (apply or restore may have changed it).
  useEffect(() => { if (!syncOpen) void refreshIgnoreCount(); }, [syncOpen, refreshIgnoreCount]);
  useEffect(() => { if (!ignoredOpen) void refreshIgnoreCount(); }, [ignoredOpen, refreshIgnoreCount]);

  const isSuperAdmin = (user?.roles ?? []).includes('SuperAdmin');

  return (
    <>
      <DomainShell
        title="Identity & Access"
        description="People who can sign in, the roles they hold, and the permissions each role grants. Changes apply when the person next opens the app (the server picks them up within 5 minutes) — no deploy required. Roles live in ROP's database; Entra only identifies the person."
        basePath="/retail/identity"
        items={SUBNAV.map((s) => ({
          slug: s.slug,
          label: s.label,
          requiredRoles: s.requiredRoles,
        }))}
        userRoles={user?.roles ?? []}
        headerActions={
          <>
            {ignoreCount !== null && ignoreCount > 0 && (
              <Button variant="ghost" onClick={() => setIgnoredOpen(true)}>
                <EyeOff size={14} /> Ignored ({ignoreCount})
              </Button>
            )}
            {isSuperAdmin && (
              <Button onClick={() => setSyncOpen(true)}>
                <RefreshCw size={14} /> Sync
              </Button>
            )}
          </>
        }
      />
      <SyncModal open={syncOpen} onClose={() => setSyncOpen(false)} />
      <IgnoredModal open={ignoredOpen} onClose={() => setIgnoredOpen(false)} />
    </>
  );
}
